import { useState } from "react";
import {
    ArrowRight,
    Loader2,
    CheckCircle2,
    Calendar as CalendarIcon,
    ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { submitStrategyCall } from "@/lib/api";
import FadeUp from "./FadeUp";

const CALENDLY_URL = "https://calendly.com/ambuj-av-consultation/30min";

const revenueBuckets = [
    "Pre-launch",
    "< $10k / month",
    "$10k – $50k / month",
    "$50k – $200k / month",
    "$200k – $1M / month",
    "$1M+ / month",
];

const initialForm = {
    name: "",
    title: "",
    company: "",
    website: "",
    email: "",
    monthly_revenue: "",
    help_with: [],
    biggest_challenge: "",
};

export const FinalCTA = () => {
    const [form, setForm] = useState(initialForm);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const update = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e?.target?.value ?? e }));

    const isValid =
        form.name.trim() &&
        form.company.trim() &&
        /^\S+@\S+\.\S+$/.test(form.email) &&
        form.monthly_revenue &&
        form.biggest_challenge.trim().length > 5;

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!isValid || loading) return;
        setLoading(true);
        try {
            await submitStrategyCall(form);
            setSuccess(true);
            setForm(initialForm);
            toast.success("Discovery call request received", {
                description: "Ambuj will reach out within 24 hours.",
            });
        } catch (err) {
            toast.error("Something went wrong", {
                description:
                    err?.response?.data?.detail ||
                    "Please try again or email ambuj@av-consultation.com",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="final-cta"
            data-testid="final-cta-section"
            className="section-pad bg-[#FAFAFA] border-y border-black/5"
        >
            <div className="container-page">
                <div className="max-w-3xl mx-auto text-center">
                    <FadeUp>
                        <p className="text-xs font-bold tracking-[0.2em] uppercase text-gray-500">
                            Book a discovery call
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.05}>
                        <h2
                            data-testid="final-cta-heading"
                            className="mt-4 font-display font-extrabold text-[40px] leading-[1.3] sm:text-5xl md:text-6xl lg:text-[60px] lg:leading-[1.3] tracking-[-0.025em] text-balance text-[#0A0A0A]"
                        >
                            Build an influencer engine your team will own, not rent.
                        </h2>
                    </FadeUp>
                    <FadeUp delay={0.12}>
                        <p className="mt-6 text-lg md:text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto text-pretty">
                            By the end of the engagement, you'll have a repeatable system for creator acquisition, UGC, affiliates, YouTube partnerships, and long-term growth. A 30-minute call. No pitch deck, no sales script.
                        </p>
                    </FadeUp>
                </div>

                <FadeUp delay={0.18}>
                    {success ? (
                        <div
                            data-testid="strategy-call-success"
                            className="mt-14 md:mt-16 max-w-2xl mx-auto bg-white rounded-3xl border border-black/5 p-8 md:p-12 shadow-[0_30px_70px_-40px_rgba(0,0,0,0.18)] text-center"
                        >
                            <div className="mx-auto h-14 w-14 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center">
                                <CheckCircle2 className="h-7 w-7" />
                            </div>
                            <h3 className="mt-6 font-display font-extrabold text-[28px] md:text-[36px] tracking-[-0.02em] leading-[1.3] text-[#0A0A0A]">
                                Thanks. We&apos;re on it.
                            </h3>
                            <p className="mt-4 text-[15px] md:text-base text-gray-600 leading-relaxed max-w-md mx-auto">
                                Your request is in. Ambuj will reach out
                                personally within{" "}
                                <span className="font-semibold text-[#0A0A0A]">
                                    24 hours
                                </span>
                                .
                            </p>

                            <div className="mt-8 rounded-2xl bg-[#0A0A0A] text-white p-6 md:p-7 text-left">
                                <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#C6F432]">
                                    Skip the wait
                                </p>
                                <p className="mt-3 font-display font-bold text-lg md:text-xl tracking-tight text-white leading-[1.3]">
                                    Book your 30-minute call now.
                                </p>
                                <p className="mt-2 text-[13.5px] text-white/60 leading-relaxed">
                                    Pick a slot that works for you.
                                </p>
                                <a
                                    href={CALENDLY_URL}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="calendly-cta"
                                    className="mt-5 inline-flex items-center gap-2 rounded-full bg-white hover:bg-[#C6F432] text-[#0A0A0A] h-11 px-5 text-sm font-semibold transition-all hover:-translate-y-[1px]"
                                >
                                    <CalendarIcon className="h-4 w-4" />
                                    Book on Calendly
                                    <ExternalLink className="h-3.5 w-3.5" />
                                </a>
                            </div>

                            <button
                                type="button"
                                data-testid="submit-another"
                                onClick={() => setSuccess(false)}
                                className="mt-6 text-sm text-gray-500 hover:text-[#0A0A0A] transition-colors underline underline-offset-4 decoration-black/10 hover:decoration-[#0A0A0A]"
                            >
                                Submit another response
                            </button>
                        </div>
                    ) : (
                        <form
                            onSubmit={onSubmit}
                            data-testid="strategy-call-form"
                            className="mt-14 md:mt-16 max-w-2xl mx-auto bg-white rounded-3xl border border-black/5 p-7 md:p-10 shadow-[0_30px_70px_-40px_rgba(0,0,0,0.18)]"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <Field label="Name" htmlFor="f-name">
                                    <Input
                                        id="f-name"
                                        data-testid="form-input-name"
                                        placeholder="Your full name"
                                        value={form.name}
                                        onChange={update("name")}
                                        required
                                        className="h-12 rounded-xl border-black/10 bg-white focus-visible:ring-[#0A0A0A]/20 focus-visible:border-[#0A0A0A]"
                                    />
                                </Field>
                                <Field label="Title" htmlFor="f-title">
                                    <Input
                                        id="f-title"
                                        data-testid="form-input-title"
                                        placeholder="Founder, Head of Marketing…"
                                        value={form.title}
                                        onChange={update("title")}
                                        className="h-12 rounded-xl border-black/10 bg-white focus-visible:ring-[#0A0A0A]/20 focus-visible:border-[#0A0A0A]"
                                    />
                                </Field>
                                <Field label="Company" htmlFor="f-company">
                                    <Input
                                        id="f-company"
                                        data-testid="form-input-company"
                                        placeholder="Brand name"
                                        value={form.company}
                                        onChange={update("company")}
                                        required
                                        className="h-12 rounded-xl border-black/10 bg-white focus-visible:ring-[#0A0A0A]/20 focus-visible:border-[#0A0A0A]"
                                    />
                                </Field>
                                <Field label="Website" htmlFor="f-website">
                                    <Input
                                        id="f-website"
                                        data-testid="form-input-website"
                                        placeholder="https://"
                                        value={form.website}
                                        onChange={update("website")}
                                        className="h-12 rounded-xl border-black/10 bg-white focus-visible:ring-[#0A0A0A]/20 focus-visible:border-[#0A0A0A]"
                                    />
                                </Field>
                                <Field label="Email" htmlFor="f-email">
                                    <Input
                                        id="f-email"
                                        data-testid="form-input-email"
                                        type="email"
                                        placeholder="you@brand.com"
                                        value={form.email}
                                        onChange={update("email")}
                                        required
                                        className="h-12 rounded-xl border-black/10 bg-white focus-visible:ring-[#0A0A0A]/20 focus-visible:border-[#0A0A0A]"
                                    />
                                </Field>
                                <Field
                                    label="Monthly revenue"
                                    htmlFor="f-revenue"
                                >
                                    <Select
                                        value={form.monthly_revenue}
                                        onValueChange={(v) =>
                                            setForm((f) => ({
                                                ...f,
                                                monthly_revenue: v,
                                            }))
                                        }
                                    >
                                        <SelectTrigger
                                            id="f-revenue"
                                            data-testid="form-select-revenue"
                                            className="h-12 rounded-xl border-black/10 bg-white text-left focus:ring-[#0A0A0A]/20 focus:border-[#0A0A0A]"
                                        >
                                            <SelectValue placeholder="Select a range" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {revenueBuckets.map((b) => (
                                                <SelectItem
                                                    key={b}
                                                    value={b}
                                                    data-testid={`revenue-option-${b}`}
                                                >
                                                    {b}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </Field>
                                <div className="md:col-span-2">
                                    <Field
                                        label="What are you stuck on?"
                                        htmlFor="f-challenge"
                                    >
                                        <Textarea
                                            id="f-challenge"
                                            data-testid="form-input-challenge"
                                            placeholder="Where do you need help"
                                            rows={4}
                                            value={form.biggest_challenge}
                                            onChange={update(
                                                "biggest_challenge"
                                            )}
                                            required
                                            className="rounded-xl border-black/10 bg-white focus-visible:ring-[#0A0A0A]/20 focus-visible:border-[#0A0A0A] resize-none"
                                        />
                                    </Field>
                                </div>
                            </div>

                            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                                <Button
                                    type="submit"
                                    data-testid="form-submit"
                                    disabled={!isValid || loading}
                                    className="w-full sm:w-auto rounded-full bg-[#0A0A0A] hover:bg-black disabled:opacity-60 disabled:cursor-not-allowed text-white h-12 px-7 text-sm font-semibold shadow-[0_16px_36px_-14px_rgba(0,0,0,0.35)] transition-all hover:-translate-y-[1px]"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Sending…
                                        </>
                                    ) : (
                                        <>
                                            Book Discovery Call
                                            <ArrowRight className="ml-2 h-4 w-4" />
                                        </>
                                    )}
                                </Button>
                                <p className="text-xs text-gray-500 leading-relaxed">
                                    Free · 30 minutes · No sales pitch
                                </p>
                            </div>
                        </form>
                    )}
                </FadeUp>
            </div>
        </section>
    );
};

const Field = ({ label, htmlFor, children }) => (
    <div>
        <Label
            htmlFor={htmlFor}
            className="block text-[13px] font-semibold text-[#0A0A0A] mb-2"
        >
            {label}
        </Label>
        {children}
    </div>
);

export default FinalCTA;
